import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import type { CheckableTrainingEventCard, TrainerMonthStatus, TrainingGroupResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card } from '../components/ui';
import { DateBadge } from '../components/DateBadge';
import { GroupFilterDropdown, WeekdayFilterDropdown } from '../components/FilterDropdowns';
import { rosterChipClass } from '../lib/roster-chip';
import { isoWeekday } from '../lib/weekday';

function formatDeadline(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function lastMonth(): { year: number; month: number } {
  const d = new Date();
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
}
function monthName(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleDateString('de-DE', { month: 'long' });
}
function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

/** "Trainer" (Arbeitsseite, § Nutzeranfrage) — bewusst die kompakteste
 * Listenseite der App: Trainer öffnen sie meist kurz nach dem Training auf
 * dem Handy, um sich einzutragen. Zeigt den gewählten Kalendermonat
 * vollständig (Monat-Vor/Zurück-Navigation, gleiches Muster wie die
 * Admin-Trainings-Übersicht in OrgTrainersPage) — dieselbe Einheiten-Menge
 * wie dort und wie auf der Trainingsseite (§ Nutzeranfrage: "sollen alle
 * Trainings erscheinen, die auch unter der Seite Training angezeigt
 * werden"), inkl. künftiger Einheiten (dafür noch kein Check-in möglich,
 * `inFuture`).
 *
 * Liste bewusst im selben Stil wie `CalendarPage.tsx`s Listenansicht
 * (`AgendaList`, § Nutzeranfrage „soll quasi genauso aussehen wie der
 * Kalender in Listenansicht"): `Card` mit `divide-y`, eine `DateBadge` je
 * Tag statt je Zeile, Zeile mit farbigem linken Akzentstreifen. Ersetzt das
 * frühere eigenständige Kartenlayout (eigene `DateBadge size="lg"` je
 * Zeile) — diese Seite ist seitdem keine Insel mehr im eigenen Look,
 * sondern teilt die Listen-Sprache mit dem Kalender. Statt eines Klicks,
 * der ein Pop-up öffnet (wie im Kalender), steht die Ein-/Austrage-Aktion
 * hier direkt in der Zeile, da sie ohnehin nur ein einzelner Button ist
 * (s. u.) — ein zusätzliches Pop-up wäre für diesen einen Tap reiner
 * Mehraufwand gewesen. Eingetragene Einheiten schimmern grün
 * (`bg-green-50`), analog zur „angemeldet"-Einfärbung im Kalender —
 * derselbe Blickfang für „hier ist schon etwas erledigt".
 *
 * **Keine Uhrzeit in der Zeile, Trainingsgruppe dafür deutlich hervorgehoben**
 * (§ Nutzeranfrage „keine Zeiten … nur die Trainingsgruppe", dann „soll
 * deutlicher erkennbar sein") — anders als `CalendarPage.tsx`s `AgendaList`,
 * die die Uhrzeit fett in einer eigenen Spalte zeigt, trägt eine Zeile hier
 * nur noch die Trainingsgruppen-Kapsel(n) (`trainingGroupNames`, leer bei
 * einer Einheit ohne Gruppenziel) und — sobald vorhanden — die
 * eingetragene-Trainer-Kapseln. Die Uhrzeit bleibt für Trainer dennoch
 * auffindbar: über die Trainingsseite selbst, wo jede Einheit weiterhin
 * ihre Zeit zeigt. Da die Gruppe seitdem die wichtigste Information der
 * Zeile ist, ist ihre Kapsel bewusst deutlich kräftiger als eine beiläufige
 * Metadaten-Kapsel: größer (`text-sm` statt `text-xs`), fett, in der
 * Akzentfarbe (`bg-brand-accent/10`/`text-brand-accent`) statt im zuvor
 * blassen, neutralen Grau.
 *
 * **Kein Stundenfeld mehr, Eintragen nur noch ein Daumen-hoch-Icon-Button**
 * (§ Nutzeranfrage „Möglichkeit eine andere Zeit einzutragen soll entfallen
 * … Anwesend-Button … nur ein Daumen hoch … somit müsste man in eine Zeile
 * passen"). Ein Tap trägt die aus Start-/Endzeit berechnete Dauer ein
 * (`CheckInRequest.hours` bleibt optional und wird hier einfach nicht mehr
 * gesendet — der Server berechnet sie dann selbst, siehe
 * `TrainersService.insertCheckIn()`); eine individuelle Korrektur bleibt
 * ausschließlich der Verwaltungsseite vorbehalten
 * (`OrgTrainersPage.tsx`, `PUT /trainers/checkins/:checkInId`, s.
 * docs/trainers.md). Ohne das Eingabefeld braucht die Aktionsspalte für den
 * unbestätigten Zustand nur noch die Breite eines einzelnen Icon-Buttons,
 * wodurch die ganze Zeile zuverlässig einzeilig bleibt, statt wie zuvor bei
 * wenig Platz umzubrechen. */
export function TrainerCheckinPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [error, setError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [weekdayFilter, setWeekdayFilter] = useState<number[]>([]);

  function shiftMonth(delta: number) {
    setMonth((m) => {
      const d = new Date(m.year, m.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  }

  const { data: events, isLoading } = useQuery({
    queryKey: ['trainers', 'checkable-events', month.year, month.month],
    queryFn: () =>
      api<CheckableTrainingEventCard[]>(`/trainers/checkable-events?year=${month.year}&month=${month.month}`, {
        orgScoped: true,
      }),
  });
  // Jeder Trainer sieht alle Gruppen zur Auswahl (nicht nur die eigenen) —
  // gleiches Muster wie der Gruppenfilter auf der Trainings-Übersicht.
  const { data: groups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });
  // Hinweis-Banner, falls für den Vormonat noch offen und eine Deadline
  // hinterlegt ist (§ Nutzeranfrage) — reine Erinnerung, das eigentliche
  // Sperren übernimmt `monthClosed` je Karte weiter unten.
  const { year: lastMonthYear, month: lastMonthMonth } = lastMonth();
  const { data: lastMonthStatus } = useQuery({
    queryKey: ['trainers', 'month-status', lastMonthYear, lastMonthMonth],
    queryFn: () => api<TrainerMonthStatus>(`/trainers/months/${lastMonthYear}/${lastMonthMonth}`, { orgScoped: true }),
  });
  // Beide Filter sind rein clientseitig auf der ohnehin geladenen Liste
  // (§ Nutzeranfrage) — gleiches Muster wie der Wochentag-Filter auf der
  // Trainings-Übersicht, kein eigener Backend-Query-Parameter nötig.
  const filteredEvents = events?.filter(
    (e) =>
      (weekdayFilter.length === 0 || weekdayFilter.includes(isoWeekday(e.eventDate))) &&
      (groupFilter.length === 0 || e.trainingGroupIds.some((id) => groupFilter.includes(id))),
  );

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['trainers', 'checkable-events'] });

  const checkIn = useMutation({
    mutationFn: (eventId: string) => api(`/trainers/events/${eventId}/checkin`, { method: 'POST', orgScoped: true, body: {} }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Eintragen fehlgeschlagen.'),
  });

  const undoCheckIn = useMutation({
    mutationFn: (eventId: string) => api(`/trainers/events/${eventId}/checkin`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Rückgängig machen fehlgeschlagen.'),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="hidden text-xl font-semibold md:block">Trainer</h1>
          <p className="text-sm text-slate-500">Trage dich hier ein, wenn du eine Trainingseinheit gegeben hast.</p>
        </div>
        {/* `flex-wrap` (§ Nutzerfeedback "Inhalt wird außerhalb des
            Content-Bereichs dargestellt", siehe SeasonPage.tsx) — ohne das
            läuft diese Gruppe auf schmalen Mobil-Viewports über die
            Bildschirmbreite hinaus. */}
        <div className="flex flex-wrap items-center gap-2">
          <GroupFilterDropdown groups={groups ?? []} selected={groupFilter} onChange={setGroupFilter} />
          <WeekdayFilterDropdown selected={weekdayFilter} onChange={setWeekdayFilter} />
        </div>
      </div>

      {error && <Alert>{error}</Alert>}

      {lastMonthStatus && !lastMonthStatus.closedAt && lastMonthStatus.deadline && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Bitte trage deine Stunden für {monthName(lastMonthMonth)} bis zum {formatDeadline(lastMonthStatus.deadline)} ein.
        </p>
      )}

      <div className="flex items-center justify-center gap-1 text-sm">
        <button
          type="button"
          aria-label="Vorheriger Monat"
          onClick={() => shiftMonth(-1)}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          ‹
        </button>
        <span className="min-w-28 text-center font-medium text-slate-700">{monthLabel(month.year, month.month)}</span>
        <button
          type="button"
          aria-label="Nächster Monat"
          onClick={() => shiftMonth(1)}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          ›
        </button>
      </div>

      {filteredEvents && filteredEvents.length > 0 && (
        <Card>
          <div className="divide-y divide-slate-100">
            {days(filteredEvents).map(({ date, items }) => (
              <div key={date} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <DateBadge date={date} />
                {/* `min-w-0` wie in CalendarPage.tsx's AgendaList — sonst
                 * weigert sich diese `flex-1`-Spalte, unter ihre
                 * Inhaltsbreite zu schrumpfen, und die Zeile läuft auf
                 * schmalen Handys über den Bildschirmrand hinaus. */}
                <div className="min-w-0 flex-1 space-y-1.5">
                  {items.map((e) => (
                    <div
                      key={e.id}
                      className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border-l-[3px] border-blue-400 py-2 pl-3 pr-2.5 ${
                        e.myCheckIn ? 'bg-green-50' : 'bg-slate-50'
                      }`}
                    >
                      {/* Bewusst ohne `min-w-0`/`flex-1` wie sonst im Projekt
                       * üblich: diese Spalte trägt nur kurze Kapseln (Gruppe,
                       * eingetragene Trainer), keinen langen Fließtext, der
                       * ein Zusammenschrumpfen bräuchte. Mit `min-w-0` durfte
                       * sie bis auf 0px schrumpfen, statt die ganze Zeile
                       * (Zeit/Inhalt/Aktion) bei zu wenig Platz als Einheit
                       * umbrechen zu lassen — auf schmalen Handys überlappte
                       * dadurch z. B. die „Test"-Kapsel sichtbar mit der
                       * grünen Stunden-Anzeige rechts daneben. */}
                      <div className="flex-1 basis-32">
                        {/* § Nutzeranfrage „Trainingsgruppe soll deutlicher
                         * erkennbar sein" — seit der Uhrzeit nicht mehr
                         * angezeigt wird (s. o.), ist die Gruppe die
                         * wichtigste Information in der Zeile; die vorherige
                         * kleine, blasse graue Kapsel (`text-xs`/`text-slate-500`,
                         * identisch zu einer reinen Metadaten-Kapsel) ging
                         * darin unter. Jetzt deutlich größer/kräftiger in der
                         * Akzentfarbe statt neutralem Grau, derselbe
                         * `bg-brand-accent/10`/`text-brand-accent`-Stil wie
                         * z. B. die aktive Filter-Kapsel oben. */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {e.trainingGroupNames.map((g) => (
                            <span
                              key={g}
                              className="rounded-full bg-brand-accent/10 px-2.5 py-1 text-sm font-semibold text-brand-accent"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                        {e.checkedInTrainers.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {e.checkedInTrainers.map((t) => (
                              <span
                                key={t.name}
                                title={t.name}
                                className={`rounded-full px-2 py-0.5 text-xs ${rosterChipClass('positive', null)}`}
                              >
                                {t.firstName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        {e.myCheckIn ? (
                          <>
                            <span className="flex h-8 items-center gap-1 whitespace-nowrap rounded-md bg-green-100 px-2 text-xs font-medium text-green-700">
                              <Check size={14} /> {e.myCheckIn.hours.toFixed(2)} Std.
                            </span>
                            <button
                              type="button"
                              className="whitespace-nowrap text-xs text-slate-400 hover:text-red-600"
                              disabled={undoCheckIn.isPending}
                              onClick={() => undoCheckIn.mutate(e.id)}
                            >
                              Rückgängig
                            </button>
                          </>
                        ) : e.inFuture ? (
                          <span className="whitespace-nowrap text-xs font-medium text-slate-400">
                            Noch nicht stattgefunden
                          </span>
                        ) : e.monthClosed ? (
                          <span className="whitespace-nowrap text-xs font-medium text-slate-400">Monat geschlossen</span>
                        ) : (
                          <Button
                            type="button"
                            title="Anwesend"
                            aria-label="Anwesend"
                            className="h-8 w-8 px-0"
                            disabled={checkIn.isPending}
                            onClick={() => checkIn.mutate(e.id)}
                          >
                            <ThumbsUp size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!isLoading && events?.length === 0 && (
        <p className="text-sm text-slate-500">Keine Trainingseinheiten im {monthLabel(month.year, month.month)}.</p>
      )}
      {events && events.length > 0 && filteredEvents?.length === 0 && (
        <p className="text-sm text-slate-500">
          Keine Trainingseinheiten für die gewählten Filter.{' '}
          <button
            type="button"
            className="text-brand-accent hover:underline"
            onClick={() => {
              setGroupFilter([]);
              setWeekdayFilter([]);
            }}
          >
            Filter zurücksetzen
          </button>
        </p>
      )}
    </div>
  );
}

/** Gruppiert die bereits nach `eventDate, startTime` sortierte Liste
 * (`TrainersService.listCheckableEvents()`) in Tagesblöcke, ohne erneut zu
 * sortieren — dieselbe Idee wie `CalendarPage.tsx`s `entriesByDate`, nur als
 * reihenfolgeerhaltendes Array statt einer `Map` (hier reicht ein einziger
 * Durchlauf, da die Liste bereits chronologisch vorsortiert ankommt). */
function days(events: CheckableTrainingEventCard[]): Array<{ date: string; items: CheckableTrainingEventCard[] }> {
  const out: Array<{ date: string; items: CheckableTrainingEventCard[] }> = [];
  for (const e of events) {
    const last = out[out.length - 1];
    if (last && last.date === e.eventDate) last.items.push(e);
    else out.push({ date: e.eventDate, items: [e] });
  }
  return out;
}
